import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, X, TrendingDown } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { BalanceResponse } from '../../../types/balance.types';
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import { clearVerification } from '../../../store/slices/emailVerificationSlice';

interface CompletionNotificationProps {
  previousBalance: BalanceResponse | null;
  currentBalance: BalanceResponse | null;
  onViewDetails?: () => void;
  searchId?: number;
}

const CompletionNotification: React.FC<CompletionNotificationProps> = ({
  previousBalance,
  currentBalance,
  onViewDetails,
  searchId,
}) => {
  const posthog = usePostHog();
  const dispatch = useAppDispatch();
  const [isVisible, setIsVisible] = useState(true);
  const { completedVerification } = useAppSelector((state) => state.emailVerification);
  const capturedRequestKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!completedVerification) {
      capturedRequestKeyRef.current = null;
      return;
    }

    const captureKey = `${completedVerification.status}:${completedVerification.summary}:${completedVerification.credits_charged}`;
    if (capturedRequestKeyRef.current === captureKey) return;
    capturedRequestKeyRef.current = captureKey;

    setIsVisible(true);
    posthog?.capture('verify_completed', {
      credits_charged: completedVerification.credits_charged,
      credits_refunded: completedVerification.credits_refunded,
      valid_count: completedVerification.results?.filter((r: any) => r.status === 'GUARANTEED').length ?? 0,
      invalid_count: completedVerification.results?.filter((r: any) => r.status === 'INVALID').length ?? 0,
      search_id: searchId,
    });

    const timer = setTimeout(() => {
      setIsVisible(false);
      dispatch(clearVerification());
    }, 8000);

    return () => clearTimeout(timer);
  }, [completedVerification, dispatch, posthog, searchId]);

  if (!completedVerification || !isVisible || !currentBalance) {
    return null;
  }

  const verificationDelta = previousBalance && currentBalance
    ? currentBalance.email_verifications_remaining - previousBalance.email_verifications_remaining
    : 0;

  return (
    <div className="fixed top-20 right-4 z-50 max-w-md animate-slide-in">
      <div className="bg-white border-l-4 border-green-500 rounded-lg shadow-lg p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1">
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900 mb-1">Verification Complete!</h4>
              <p className="text-sm text-gray-700 mb-2">{completedVerification.summary}</p>
              {onViewDetails && (
                <button
                  type="button"
                  onClick={() => {
                    onViewDetails();
                    setIsVisible(false);
                  }}
                  className="text-sm font-medium text-primary hover:text-primary/80 mb-2"
                >
                  View details
                </button>
              )}
              {previousBalance && (
                <div className="flex items-center gap-2 text-sm">
                  <TrendingDown className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-600">
                    Verifications: {previousBalance.email_verifications_remaining.toLocaleString()} → {currentBalance.email_verifications_remaining.toLocaleString()}
                    <span className="font-medium text-red-600 ml-1">
                      ({verificationDelta})
                    </span>
                  </span>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={() => { setIsVisible(false); dispatch(clearVerification()); }}
            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompletionNotification;
